import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import { createRef, FC, useEffect } from "react";
import { P } from "theme/typography";
import PageNumberInput from "components/PageNumberInput";
import theme from "theme";

interface PaginatorProps {
  value: number;
  setValue: (page: number) => void;
  min: number;
  max: number;
  bindArrowKeys?: boolean;
}

export const Paginator: FC<PaginatorProps> = (props) => {
  const { value, setValue, min, max, bindArrowKeys } = props;
  const canPreviousPage = value > 1;
  const canNextPage = value < max;
  const inputRef = createRef<HTMLInputElement>();

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current?.focus();
    }
  };

  const nextPage = () => {
    if (canNextPage) {
      setValue(value + 1);
    }
  };
  const prevPage = () => {
    if (canPreviousPage) {
      setValue(value - 1);
    }
  };

  useEffect(() => {
    const listener = (ev: KeyboardEvent) => {
      const onInput = ev.target && (ev.target["tagName"] === "INPUT" || ev.target["tagName"] === "TEXTAREA");

      if (!onInput && bindArrowKeys) {
        if (ev.key === "ArrowRight") {
          nextPage();
        } else if (ev.key === "ArrowLeft") {
          prevPage();
        }
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  });

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        "&:hover": {
          input: {
            backgroundColor: theme.palette.grey[50],
          },
        },
      }}>
      <IconButton
        disabled={!canPreviousPage}
        onClick={prevPage}
        size="small"
        data-pendo="paginator-previous"
        title={`Previous Page (${"\u2190"})`}>
        <NavigateBefore />
      </IconButton>
      <PageNumberInput
        dataPendo="paginator-number-input"
        inputRef={inputRef}
        min={min}
        max={max}
        updateNumber={setValue}
        value={value}
      />
      <div className="flex place-items-center cursor-pointer h-[24px]" onClick={focusInput}>
        <P sx={{ color: "grey.400", mr: "8px" }}>/</P>
        <P sx={{ color: "grey.400", mr: "4px" }}>{max}</P>
      </div>
      <IconButton
        disabled={!canNextPage}
        onClick={nextPage}
        size="small"
        data-pendo="paginator-next"
        title={`Next Page (${"\u2192"})`}>
        <NavigateNext />
      </IconButton>
    </Box>
  );
};
